import { format } from 'date-fns';
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { Unit } from '../../../components/ServiceItem';
import { UnitDate } from '../../../contexts/ScheduleContext';
import { CoverageProps, Rule } from '../../../models/Product';
import { ScheduleItemProps, typeLocationSchedule } from '../../../models/Schedule';
import { getOpenToScheduleRuleId } from '../../../utils/catalogRules';
import { getRangeDateFromToday, getRangeDatesOfRule } from '../../../utils/dates';
import { fetchDateSlots } from '../../../modules/slots';

type useAttendanceUnitsOutput = {
  loading: boolean;
  openToScheduleUnits: Unit[];
  closedToScheduleUnits: Unit[];
  unitDatesAvailable: UnitDate[];
};

export const useAttendanceUnitsWithDates = (
  coverage: CoverageProps | undefined,
  schedule: ScheduleItemProps | undefined
): useAttendanceUnitsOutput => {
  const [loading, setLoading] = useState(false);
  const [unitDatesAvailable, setUnitDatesAvailable] = useState<UnitDate[]>([]);
  const [openToScheduleUnits, setOpenToScheduleUnits] = useState<Unit[]>([]);
  const [closedToScheduleUnits, setClosedToScheduleUnits] = useState<Unit[]>([]);

  function isUnitOpenToSchedule({ unit, rules }: { rules: Rule[]; unit: Unit }) {
    const unableToScheduleRule = rules.find(rule => rule.ruleId === getOpenToScheduleRuleId());
    const idsUnavailableToSchedule: [] = unableToScheduleRule?.ruleValue
      ? JSON.parse(String(unableToScheduleRule.ruleValue))
      : [];

    return !idsUnavailableToSchedule.find(id => {
      return String(id) === unit.id_sgh;
    });
  }

  const hasAvailableDatesUnit = (unitDates: string[]) => {
    let availableSchedule = false;

    availableSchedule = availableSchedule || !!unitDates;

    return availableSchedule;
  };

  const executePromisses = async (requestsPromises: Promise<UnitDate | undefined>[]) => {
    let availableSchedule = false;
    (await Promise.all(requestsPromises)).map(request => {
      if (request && schedule) {
        const isAvailableToSchedule = hasAvailableDatesUnit(request.dates);
        availableSchedule = availableSchedule || isAvailableToSchedule;
      }
    });

    return availableSchedule;
  };

  const getDatesByUnit = async ({
    unitIdSGH,
    itemId,
    isKit,
    resourceId,
    rules,
    localId
  }: {
    unitIdSGH: string;
    itemId: string;
    isKit: boolean;
    resourceId: string | number | undefined;
    rules: Rule[];
    localId: string;
  }) => {
    try {
      const daysToRange = getRangeDatesOfRule(rules);
      const { today, endDate } = getRangeDateFromToday(daysToRange);
      const { slotDates }: { slotDates: string[] } = await fetchDateSlots({
        unitId: unitIdSGH,
        itemId,
        isKit,
        startDate: format(today, 'yyyy-MM-dd'),
        endDate: format(endDate, 'yyyy-MM-dd'),
        resourceId,
        localId
      });
      return slotDates;
    } catch (error) {
      toast.error('Falha ao carregar dados. Tente novamente mais tarde.');
    }
  };

  const preperToFetchSlots = (coverage: CoverageProps, unit: Unit, unitDates: UnitDate[]) => {
    const resourceId = unit.resources?.[0].name ?? '';

    return async () => {
      const unitInfo = await getDatesByUnit({
        unitIdSGH: unit.id_sgh,
        itemId: coverage.item_SGH_id,
        isKit: coverage.is_kit,
        resourceId,
        rules: coverage.rules,
        localId: unit?.local_id || unit?.locations?.[0]?.name || ''
      });

      if (unitInfo) {
        const unitDatesAvailable = { unitSghId: unit.id, dates: unitInfo, resourceId } as UnitDate;

        unitDates.push(unitDatesAvailable);

        return unitDatesAvailable;
      }
    };
  };

  const loadUnitDates = async (coverage: CoverageProps) => {
    setLoading(true);

    const { is_kit, id: coverageID, rules, care_type, item_SGH_id } = coverage;

    let units = [] as Unit[];
    if (coverage && coverage.units) {
      units = coverage.units;
    }

    if (units) {
      setOpenToScheduleUnits([]);
      setClosedToScheduleUnits([]);

      const unitDatesAvailable = [] as UnitDate[];

      const openToScheduleUnits = [] as Unit[];
      const closedToScheduleUnits = [] as Unit[];
      const requestsPromises: Promise<UnitDate | undefined>[] = [];

      units.forEach(async unit => {
        unit.isKit = is_kit;
        unit.itemId = item_SGH_id;
        unit.catalogItemId = coverageID;
        let idUnitOpenSchedule = true;

        if (care_type === typeLocationSchedule.PRESENTIAL)
          idUnitOpenSchedule = isUnitOpenToSchedule({
            unit,
            rules: rules
          });

        if (care_type === typeLocationSchedule.EINSTEIN_TO_YOU) idUnitOpenSchedule = false;

        if (idUnitOpenSchedule) {
          const addUnitsInfoArray = preperToFetchSlots(coverage, unit, unitDatesAvailable);
          if (addUnitsInfoArray) requestsPromises.push(addUnitsInfoArray());

          openToScheduleUnits.push(unit);
        } else closedToScheduleUnits.push(unit);
      });

      await executePromisses(requestsPromises);

      setUnitDatesAvailable(unitDatesAvailable);
      setOpenToScheduleUnits(openToScheduleUnits);
      setClosedToScheduleUnits(closedToScheduleUnits);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (coverage?.care_type) {
      loadUnitDates(coverage);
    }
  }, [coverage, schedule?.resource?.id]);

  return {
    loading,
    openToScheduleUnits,
    closedToScheduleUnits,
    unitDatesAvailable
  };
};
